import type { ReactNode } from "react";
import { Button, Popconfirm, Space, Tag, Tooltip } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { partesFechaConDia } from "../../../utils/formatDate";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import { UserCell } from "../../../Components/UserAvatar";
import EstadiaCodeTag from "../../../Components/EstadiaCodeTag";
import AseoCodeTag from "../../../Components/AseoCodeTag";

interface BuildAseosColumnsDeps {
  isAdmin: boolean;
  isAseador: boolean;
  currentUserId: number | undefined;
  currentUserNombre: string | undefined;
  openTareasModal: (record: any) => void;
  openImagesModal: (urls: any) => void;
  showAseoModal: (record: any) => void;
  handleDeleteAseo: (id: number) => void;
  tomarAseo: (id: number, aseadoraId: number, aseadoraNombre: string) => void;
  /** Abre el modal de las 3 notas del aseo (admin ve/edita las 3; aseador las suyas). */
  openNotasModal: (record: any) => void;
  /**
   * Pantalla táctil (puntero grueso): sin tooltips en las acciones. Con el dedo el
   * tooltip no se va al soltar: queda pegado y tapaba el panel de notas recién
   * abierto. Round 2026-10-26.
   */
  tactil?: boolean;
}

/**
 * Ancho de «Fecha inicio» para el admin. La fecha más larga en una línea,
 * «Miércoles, septiembre 30», mide 194 px en Montserrat 600 de 15 px; se suman
 * los 8 px de padding de cada lado y un margen por variaciones de la fuente. La
 * tabla del admin en aseos.tsx crece lo mismo que esta columna
 * (ANCHO_TABLA_ADMIN), así las demás no pierden ancho.
 */
export const ANCHO_FECHA_INICIO_ADMIN = 224;

/**
 * Ancho de las tablas del admin en /aseos. Tienen ancho fijo y table-layout
 * fixed (por la columna Observaciones con ellipsis): el ancho que no se asigna
 * se reparte entre las columnas sin ancho. Antes de mostrar el día de la semana,
 * «Fecha inicio» era una de ellas y medía 78 px, igual que las demás; la tabla
 * crece exactamente lo que creció esa columna, así los montos y los tags siguen
 * cabiendo en una línea. Round 2026-09-14.
 */
export const ANCHO_TABLA_ADMIN = 1400 + (ANCHO_FECHA_INICIO_ADMIN - 78);

export function buildAseosColumns({
  isAdmin,
  isAseador,
  currentUserId,
  currentUserNombre,
  openTareasModal,
  openImagesModal,
  showAseoModal,
  handleDeleteAseo,
  tomarAseo,
  openNotasModal,
  tactil = false,
}: BuildAseosColumnsDeps): any[] {
  return [
    {
      // Código de aseo propio (aseos.id): identificador único e histórico del
      // aseo en sí, distinto del «Estadía #N» del arriendo vinculado. Siempre
      // presente (todo aseo tiene id), incluso los aledaños/manuales.
      title: "Aseo",
      dataIndex: "id",
      key: "aseoCode",
      // Ancho fijo: el Tag es `white-space: nowrap`; sin reservar su ancho, la
      // grilla admin (scroll.x acotado) lo comprimía y el «Aseo #N» se
      // superponía con la columna vecina (fix 2026-07-16).
      width: 120,
      sorter: (a: any, b: any) => Number(a?.id ?? 0) - Number(b?.id ?? 0),
      render: (id: any) => <AseoCodeTag id={id} withTooltip={false} />,
    },
    {
      title: "Aseador",
      dataIndex: "nombreAseador",
      key: "nombreAseador",
      sorter: (a: any, b: any) =>
        (a.nombreAseador ?? "").localeCompare(b.nombreAseador ?? ""),
      // Round 2026-08-25 — equipo 1..N: la responsable con carita, y las
      // acompañantes debajo en texto chico ("+ Rosa"). El reparto de montos
      // no se muestra acá (vive en el modal de edición, admin-only).
      render: (nombre: any, record: any) => {
        if (!nombre) {
          return <span style={{ fontStyle: "italic", color: "#1890ff" }}>Abierto</span>;
        }
        const acompanantes: string[] = (record?.nombresAseadoras ?? []).slice(1);
        return (
          <div>
            <UserCell
              nombre={nombre}
              avatarSeed={record?.avatarAseador}
              avatarUrl={record?.avatarUrlAseador}
              size={26}
            />
            {acompanantes.length > 0 && (
              <div style={{ fontSize: 12, color: "#6b7280", marginLeft: 32 }}>
                {acompanantes.map((n) => `+ ${n}`).join("  ")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Propiedad",
      dataIndex: "propiedad",
      key: "propiedad",
      sorter: (a: any, b: any) =>
        String(a?.propiedad ?? "").localeCompare(String(b?.propiedad ?? "")),
      render: (text: any) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {formatPropertyName(text) || <span style={{ color: "#999" }}>—</span>}
        </div>
      ),
    },
    {
      // Código de estadía AF del arriendo vinculado (reservaId → booking.id).
      // "Manual" cuando el aseo es aledaño/manual (sin reserva). Deja cruzar el
      // aseo con su arriendo por el mismo #N en todo el sistema.
      title: "Estadía",
      dataIndex: "reservaId",
      key: "estadia",
      // Ancho fijo (mismo motivo que la columna «Aseo»): el «Estadía #N» es un
      // Tag nowrap que se superponía con «Sector» al comprimirse (fix 2026-07-16).
      width: 130,
      sorter: (a: any, b: any) =>
        Number(a?.reservaId ?? 0) - Number(b?.reservaId ?? 0),
      render: (_: any, record: any) =>
        record?.reservaId != null ? (
          <EstadiaCodeTag id={record.reservaId} withTooltip={false} />
        ) : (
          <span style={{ color: "#999" }}>Manual</span>
        ),
    },
    {
      title: "Sector",
      dataIndex: "sector",
      key: "sector",
      sorter: (a: any, b: any) =>
        String(a?.sector ?? "").localeCompare(String(b?.sector ?? "")),
    },
    {
      title: "Fecha inicio",
      dataIndex: "fechaInicio",
      key: "fechaInicio",
      // Admin: con el día de la semana y un poco más destacada («Lunes,
      // septiembre 14»; el año solo si no es el actual), para ubicar el aseo en
      // la semana de un vistazo. El aseador conserva el formato corto: su vista
      // es compacta y la usa sobre todo en el teléfono. Round 2026-09-14.
      width: isAdmin ? ANCHO_FECHA_INICIO_ADMIN : undefined,
      sorter: (a: any, b: any) =>
        dayjs(a.fechaInicio).unix() - dayjs(b.fechaInicio).unix(),
      render: (fecha: string) => {
        if (!isAdmin) return dayjs(fecha).format("DD-MM-YYYY");
        const partes = partesFechaConDia(fecha);
        if (!partes) return "—";
        // El día de la semana y el número van siempre en una línea: nunca
        // «Miércoles, septiembre» arriba y un «30» suelto abajo. El año, si no
        // es el actual, va debajo y más chico.
        return (
          <div style={{ whiteSpace: "nowrap", lineHeight: 1.35 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>
              {partes.dia}
            </div>
            {partes.anio != null && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>{partes.anio}</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Fecha final",
      dataIndex: "fechaFin",
      key: "fechaFin",
      sorter: (a: any, b: any) =>
        dayjs(a.fechaFin).unix() - dayjs(b.fechaFin).unix(),
      render: (fecha: string) => dayjs(fecha).format("DD-MM-YYYY"),
    },
    {
      title: "Horario",
      key: "horario",
      sorter: (a: any, b: any) =>
        String(a?.horaInicio ?? "").localeCompare(String(b?.horaInicio ?? "")),
      render: (record: any) => record?.horaInicio ?? "—",
    },
    {
      title: "Pago al aseador",
      key: "sueldoAseador",
      render: (_: any, record: any) =>
        record.sueldoAseador != null ? (
          <span style={{ fontWeight: "bold", color: "#389e0d" }}>
            ${Number(record.sueldoAseador).toLocaleString("es-CL")}
          </span>
        ) : (
          "No definido"
        ),
    },
    {
      title: "Tareas",
      dataIndex: "tareas",
      key: "tareas",
      width: isAseador ? 220 : 340,
      ellipsis: true,
      render: (_: any, record: any) => {
        const tareas: string[] = Array.isArray(record?.tareas)
          ? record.tareas
          : [];
        if (!tareas.length) {
          // Para aseador: dejar claro que no hay pendientes (verde) en vez
          // de un guion ambiguo que parece dato faltante.
          if (isAseador) {
            return <Tag color="green">Sin tareas</Tag>;
          }
          return <span style={{ color: "#999" }}>—</span>;
        }

        // Aseador: tag prominente clickeable que abre Modal con detalle
        // completo (descripciones largas + galería de imágenes/videos).
        // Las tareas son lo que tiene que resolver mientras hace el aseo
        // — debe destacarse y permitir lectura completa, no quedar
        // truncado en una celda. Ver openTareasModal abajo.
        if (isAseador) {
          return (
            <Tag
              color="orange"
              style={{ cursor: "pointer", fontSize: 13 }}
              onClick={() => openTareasModal(record)}
            >
              {tareas.length} {tareas.length === 1 ? "tarea" : "tareas"}
            </Tag>
          );
        }

        // Admin: vista compacta inline (sin cambios de diseño previo).
        // Las tareas pueden venir como objetos detallados desde backend;
        // extraemos `descripcion` para el render plano que admin esperaba.
        const tareasTexto = tareas
          .map((t: any) => (typeof t === "string" ? t : t?.descripcion || ""))
          .filter(Boolean);
        return (
          <Tooltip
            title={
              <div style={{ maxWidth: 520, whiteSpace: "pre-wrap" }}>
                {tareasTexto.map((t: string, i: number) => (
                  <div key={i}>• {t}</div>
                ))}
              </div>
            }
            placement="topLeft"
          >
            <div
              style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical" as any,
                WebkitLineClamp: 3,
                overflow: "hidden",
              }}
            >
              {tareasTexto.join(" • ")}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Imágenes",
      dataIndex: "imagenes",
      key: "imagenes",
      width: 120,
      render: (_: any, record: any) => {
        const imgs = Array.isArray(record?.imagenes) ? record.imagenes : [];
        const n = imgs.length;
        if (!n) return <span style={{ color: "#999" }}>—</span>;
        return (
          <Button type="link" onClick={() => openImagesModal(imgs)}>
            Ver ({n})
          </Button>
        );
      },
    },
    {
      title: "Costo de Limpieza",
      dataIndex: "costoLimpieza",
      key: "costoLimpieza",
      render: (_: any, record: any) =>
        record.costoLimpieza != null ? (
          <span style={{ fontWeight: "bold", color: "#389e0d" }}>
            ${Number(record.costoLimpieza).toLocaleString("es-CL")}
          </span>
        ) : (
          "No definido"
        ),
    },
    {
      title: "Urgencia",
      dataIndex: "urgencia",
      key: "urgencia",
      sorter: (a: any, b: any) =>
        String(a?.urgencia ?? "").localeCompare(String(b?.urgencia ?? "")),
      render: (urgencia: string) => {
        let label: string;
        let tagColor: string;

        switch (urgencia) {
          case "am":
            label = "AM";
            tagColor = "orange";
            break;
          case "pm":
            label = "PM";
            tagColor = "blue";
            break;
          case "critico 11:00":
            label = "Crítico";
            tagColor = "red";
            break;
          default:
            label = urgencia?.toUpperCase();
            tagColor = "green";
            break;
        }

        return <Tag color={tagColor}>{label}</Tag>;
      },
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      sorter: (a: any, b: any) =>
        String(a?.estado ?? "").localeCompare(String(b?.estado ?? "")),
      render: (estado: string) => {
        let label: string;
        let tagColor: string;
        switch (estado) {
          case "pendiente":
            label = "Pendiente";
            tagColor = "red";
            break;
          case "en_proceso":
            label = "En Proceso";
            tagColor = "orange";
            break;
          case "completado":
            label = "Completado";
            tagColor = "blue";
            break;
          case "pagado":
            label = "Pagado";
            tagColor = "green";
            break;
          default:
            label = "Rechazado";
            tagColor = "default";
        }

        return <Tag color={tagColor}>{label}</Tag>;
      },
    },
    {
      title: "Observaciones",
      dataIndex: "observaciones",
      key: "observaciones",
      width: 260,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      // Round 2026-05-13 — única columna de flujo financiero del aseo
      // (eliminamos "Pagado a" y "Rol AF"). El destinatario es
      // implícito: aseador asignado recibe sueldo, lavandería asociada
      // recibe lavandería, AF Empresa retiene transporte+materiales si
      // el pagador es externo. Ver docs/flujo-financiero.md §6.5.
      title: "Pagado por",
      dataIndex: "quienPaga",
      key: "quienPaga",
      sorter: (a: any, b: any) =>
        (a.quienPaga ?? "").localeCompare(b.quienPaga ?? ""),
      render: (nombre: any) =>
        nombre || (
          <span style={{ fontStyle: "italic", color: "#1890ff" }}>
            No especificado
          </span>
        ),
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_: any, record: any) => {
        // Botón de NOTAS: disponible para TODOS (admin ve/edita las 3 notas;
        // aseador ve la "para aseador" y edita la suya). Ver AseoNotesSection.
        const botonNotas = (
          <Button
            className="nm-btn-notas"
            data-testid="nm-btn-notas"
            icon={<FileTextOutlined />}
            onClick={() => openNotasModal(record)}
            type="link"
          >
            Notas
          </Button>
        );
        const notasBtn = tactil ? (
          botonNotas
        ) : (
          <Tooltip
            title={
              isAdmin
                ? "Notas del aseo: privada (solo admin), para el aseador, y las del aseador"
                : "Notas del aseo: instrucciones del administrador y tus observaciones (fotos/videos)"
            }
          >
            {botonNotas}
          </Tooltip>
        );

        // Nodo específico por rol/estado (edit/delete admin, o tomar/asignado aseador).
        let roleNode: ReactNode = null;
        if (isAdmin) {
          roleNode = (
            <>
              <Tooltip title="Editar aseo">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => showAseoModal(record)}
                  type="link"
                />
              </Tooltip>
              <Popconfirm
                title="¿Está seguro de eliminar este aseo?"
                onConfirm={() => handleDeleteAseo(record.id)}
                okText="Sí"
                cancelText="No"
              >
                <Button icon={<DeleteOutlined />} danger type="link" />
              </Popconfirm>
            </>
          );
        } else {
          const currentAseadoraId = currentUserId;
          const currentAseadoraNombre = currentUserNombre;
          if (
            record.modoAsignacion === "abierto" &&
            !record.asignacionActiva &&
            !record.aseadorasRechazados?.includes(currentAseadoraId)
          ) {
            roleNode = (
              <Popconfirm
                title="¿Está seguro de tomar este aseo?."
                onConfirm={() => {
                  if (
                    typeof currentAseadoraId === "number" &&
                    typeof currentAseadoraNombre === "string"
                  ) {
                    tomarAseo(record.id, currentAseadoraId, currentAseadoraNombre);
                  }
                }}
                okText="Sí, aceptar"
                cancelText="No"
              >
                <Button type="primary" size="small">
                  Tomar aseo
                </Button>
              </Popconfirm>
            );
          } else if (
            record.idAseador === currentAseadoraId &&
            record.asignacionActiva
          ) {
            // Aseo asignado al aseador actual: ya NO ofrecemos "Rechazar" (ver
            // nota histórica). Si necesita salir, coordina con el admin.
            const asignado = (
              <span
                style={{ color: "#52c41a", fontWeight: 500 }}
                // Táctil: sin tooltip (se queda pegado); el texto va como title.
                title={
                  tactil
                    ? "Aseo asignado a ti. Si necesitas salirte, contacta al administrador."
                    : undefined
                }
              >
                Asignado
              </span>
            );
            roleNode = tactil ? (
              asignado
            ) : (
              <Tooltip title="Aseo asignado a ti. Si necesitas salirte, contacta al administrador.">
                {asignado}
              </Tooltip>
            );
          } else {
            roleNode = <span style={{ color: "#8c8c8c" }}>No disponible</span>;
          }
        }

        return (
          <Space size={0} wrap>
            {notasBtn}
            {roleNode}
          </Space>
        );
      },
    },
  ];
}
